import type {
  AdvancedCanvasDropIndicator,
  AdvancedCanvasRect,
  AdvancedCanvasSlotOverlay,
  AdvancedCanvasToolbarSize,
  AdvancedCanvasViewportRect,
} from "./types";

type AdvancedCanvasToolbarPosition = {
  left: number;
  top: number;
};

function advancedCanvasRectsIntersect(
  left: AdvancedCanvasRect,
  right: AdvancedCanvasRect,
): boolean {
  return left.left < right.left + right.width
    && left.left + left.width > right.left
    && left.top < right.top + right.height
    && left.top + left.height > right.top;
}

export function clampAdvancedCanvasNumber(
  value: number,
  min: number,
  max: number,
): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Mirrors the advanced storefront editor toolbar placement. The candidate
 * order is part of the interaction contract: above, below, right, then left.
 */
export function advancedCanvasToolbarPosition(
  rect: AdvancedCanvasRect,
  toolbarSize: AdvancedCanvasToolbarSize,
  viewport: AdvancedCanvasViewportRect | null,
): AdvancedCanvasToolbarPosition;
export function advancedCanvasToolbarPosition(
  rect: AdvancedCanvasRect,
  toolbarSize: AdvancedCanvasToolbarSize,
  viewport: AdvancedCanvasViewportRect | null,
  avoidRects: readonly AdvancedCanvasRect[],
): AdvancedCanvasToolbarPosition | null;
export function advancedCanvasToolbarPosition(
  rect: AdvancedCanvasRect,
  toolbarSize: AdvancedCanvasToolbarSize,
  viewport: AdvancedCanvasViewportRect | null,
  avoidRects: readonly AdvancedCanvasRect[] = [],
): AdvancedCanvasToolbarPosition | null {
  const gap = 8;
  const safeInset = 8;
  const width = Math.max(1, toolbarSize.width);
  const height = Math.max(1, toolbarSize.height);
  const viewportLeft = viewport?.left ?? 0;
  const viewportTop = viewport?.top ?? 0;
  const viewportWidth = viewport?.width
    ?? Math.max(width + safeInset * 2, rect.left + rect.width + safeInset);
  const viewportHeight = viewport?.height
    ?? Math.max(height + safeInset * 2, rect.top + rect.height + safeInset);
  const minLeft = viewportLeft + safeInset;
  const maxLeft = Math.max(
    minLeft,
    viewportLeft + viewportWidth - width - safeInset,
  );
  const minTop = viewportTop + safeInset;
  const maxTop = Math.max(
    minTop,
    viewportTop + viewportHeight - height - safeInset,
  );
  const centeredLeft = rect.left + ((rect.width - width) / 2);
  const centeredTop = rect.top + ((rect.height - height) / 2);
  const candidates = [
    { left: centeredLeft, top: rect.top - height - gap },
    { left: centeredLeft, top: rect.top + rect.height + gap },
    { left: rect.left + rect.width + gap, top: centeredTop },
    { left: rect.left - width - gap, top: centeredTop },
  ];
  const fittingCandidate = candidates.find((candidate) => {
    const candidateRect: AdvancedCanvasRect = {
      height,
      left: candidate.left,
      top: candidate.top,
      width,
    };

    return candidate.left >= minLeft
      && candidate.left <= maxLeft
      && candidate.top >= minTop
      && candidate.top <= maxTop
      && avoidRects.every((avoidRect) => (
        !advancedCanvasRectsIntersect(candidateRect, avoidRect)
      ));
  });

  if (fittingCandidate) {
    return {
      left: Math.round(fittingCandidate.left),
      top: Math.round(fittingCandidate.top),
    };
  }

  if (avoidRects.length > 0) {
    return null;
  }

  const spaceAbove = Math.max(0, rect.top - minTop);
  const spaceBelow = Math.max(
    0,
    viewportTop + viewportHeight - safeInset - (rect.top + rect.height),
  );
  const fallbackTop = spaceAbove >= spaceBelow ? minTop : maxTop;

  return {
    left: Math.round(
      clampAdvancedCanvasNumber(centeredLeft, minLeft, maxLeft),
    ),
    top: Math.round(fallbackTop),
  };
}

export function advancedCanvasRectIntersectsViewport(
  rect: AdvancedCanvasRect,
  viewport: AdvancedCanvasViewportRect | null,
): boolean {
  return viewport === null
    || advancedCanvasRectsIntersect(rect, viewport);
}

/**
 * Removes the area occupied by a right-docked or bottom-docked inspector.
 * Both rectangles use the same canvas coordinate space.
 */
export function advancedCanvasViewportExcludingOccluder(
  viewport: AdvancedCanvasViewportRect,
  occluder: AdvancedCanvasRect | null,
): AdvancedCanvasViewportRect {
  if (!occluder || !advancedCanvasRectsIntersect(viewport, occluder)) {
    return viewport;
  }

  const viewportRight = viewport.left + viewport.width;
  const viewportBottom = viewport.top + viewport.height;
  const occluderRight = occluder.left + occluder.width;
  const occluderBottom = occluder.top + occluder.height;
  const intersectionLeft = Math.max(viewport.left, occluder.left);
  const intersectionTop = Math.max(viewport.top, occluder.top);
  const intersectionRight = Math.min(viewportRight, occluderRight);
  const intersectionBottom = Math.min(viewportBottom, occluderBottom);
  const intersectionWidth = Math.max(0, intersectionRight - intersectionLeft);
  const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop);
  const edgeTolerance = 1;
  const dockedRight = occluderRight >= viewportRight - edgeTolerance
    && intersectionHeight >= viewport.height - edgeTolerance;
  const dockedBottom = occluderBottom >= viewportBottom - edgeTolerance
    && intersectionWidth >= viewport.width - edgeTolerance;

  return {
    ...viewport,
    height: dockedBottom
      ? Math.max(0, intersectionTop - viewport.top)
      : viewport.height,
    width: dockedRight
      ? Math.max(0, intersectionLeft - viewport.left)
      : viewport.width,
  };
}

export function advancedCanvasToolbarBelowPosition(
  rect: AdvancedCanvasRect,
  toolbarSize: AdvancedCanvasToolbarSize,
  viewport: AdvancedCanvasViewportRect | null,
): { left: number; top: number } {
  const gap = 8;
  const safeInset = 8;
  const width = Math.max(1, toolbarSize.width);
  const height = Math.max(1, toolbarSize.height);
  const viewportLeft = viewport?.left ?? 0;
  const viewportTop = viewport?.top ?? 0;
  const viewportWidth = viewport?.width
    ?? Math.max(width + safeInset * 2, rect.left + rect.width + safeInset);
  const viewportHeight = viewport?.height
    ?? Math.max(height + safeInset * 2, rect.top + rect.height + safeInset);
  const minLeft = viewportLeft + safeInset;
  const maxLeft = Math.max(
    minLeft,
    viewportLeft + viewportWidth - width - safeInset,
  );
  const minTop = viewportTop + safeInset;
  const maxTop = Math.max(
    minTop,
    viewportTop + viewportHeight - height - safeInset,
  );
  const preferredLeft = rect.left + Math.max(0, (rect.width - width) / 2);
  const preferredTop = rect.top + rect.height + gap;
  const fallbackTop = rect.top - height - gap;
  const top = preferredTop <= maxTop ? preferredTop : fallbackTop;

  return {
    left: Math.round(
      clampAdvancedCanvasNumber(preferredLeft, minLeft, maxLeft),
    ),
    top: Math.round(clampAdvancedCanvasNumber(top, minTop, maxTop)),
  };
}

export function advancedCanvasDropIndicatorsEqual(
  left: AdvancedCanvasDropIndicator | null,
  right: AdvancedCanvasDropIndicator | null,
): boolean {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return left.height === right.height
    && Boolean(left.blocked) === Boolean(right.blocked)
    && left.left === right.left
    && left.mode === right.mode
    && left.top === right.top
    && left.width === right.width;
}

export function advancedCanvasSlotOverlaysEqual(
  left: AdvancedCanvasSlotOverlay[],
  right: AdvancedCanvasSlotOverlay[],
): boolean {
  if (left === right) {
    return true;
  }

  if (left.length !== right.length) {
    return false;
  }

  return left.every((overlay, index) => {
    const other = right[index];

    return other !== undefined
      && overlay.active === other.active
      && overlay.blocked === other.blocked
      && overlay.height === other.height
      && overlay.key === other.key
      && overlay.kind === other.kind
      && overlay.left === other.left
      && overlay.top === other.top
      && overlay.width === other.width;
  });
}
